import {v4 as uuidv4} from 'uuid'
import {PlusIcon, ReceiptPercentIcon, TrashIcon} from '@heroicons/react/24/outline'
import Dropdown from '../../../../../../components/general/Dropdown.tsx'
import MoneyInput from '../../../../../../components/general/MoneyInput/index.ts'
import {cn} from '../../../../../../lib/utils.ts'
import {fieldVariants} from '../../../../../../components/general/fieldStyles.ts'
import {decimalToLocalizedString, DisplayLocale, fromDecimalToMoney, isDecimalInSafeRange, toDecimalFromMoney, toMoneyFromCents} from '../../../../../../lib/Money.ts'
import {DropdownVATOptions} from '../../../../../../types/enums.ts'
import {DEFAULT_CURRENCY} from '../../../../../../types/constants.ts'
import {CatalogItem} from '../../../../../../types/catalogue.ts'
import {DEFAULT_LINE_UNIT, InvoiceLineItem, unitOptions, vatOptions} from '../types.ts'
import {lineTotal} from '../utils/invoiceLines.ts'
import {LineDescriptionField} from './LineDescriptionField.tsx'

export function newEmptyLine(overrides: Partial<InvoiceLineItem> = {}): InvoiceLineItem {
    return {
        id: uuidv4(),
        description: '',
        quantity: 1,
        unit: DEFAULT_LINE_UNIT,
        unitPrice: null,
        vat: DropdownVATOptions.STANDARD,
        catalogueItemId: null,
        saveToCatalogue: false,
        ...overrides,
    }
}

export function lineFromCatalogItem(item: CatalogItem): Partial<InvoiceLineItem> {
    return {
        description: item.name,
        unit: item.unit,
        unitPrice: toDecimalFromMoney(toMoneyFromCents(item.unit_price_cents, item.currency)),
        vat: item.vat_option,
        catalogueItemId: item.id,
        saveToCatalogue: false,
    }
}

type LineItemsSectionProps = {
    lines: InvoiceLineItem[]
    onChange: (lines: InvoiceLineItem[]) => void
    catalogItems: CatalogItem[]
    linesError?: string
}

export function LineItemsSection({lines, onChange, catalogItems, linesError}: LineItemsSectionProps) {

    function updateLine(id: string, patch: Partial<InvoiceLineItem>) {
        onChange(lines.map(line => line.id === id ? {...line, ...patch} : line))
    }

    function changeDescription(line: InvoiceLineItem, description: string) {
        const match = catalogItems.find(
            item => item.name.trim().toLowerCase() === description.trim().toLowerCase(),
        )

        if (!match) {
            updateLine(line.id, {description, catalogueItemId: null})
            return
        }

        updateLine(line.id, line.unitPrice === null
            ? {...lineFromCatalogItem(match), description}
            : {description, catalogueItemId: match.id, saveToCatalogue: false})
    }

    function pickCatalogItem(line: InvoiceLineItem, item: CatalogItem) {
        updateLine(line.id, lineFromCatalogItem(item))
    }

    function deleteLine(id: string) {
        const remaining = lines.filter(line => line.id !== id)
        onChange(remaining.length > 0 ? remaining : [newEmptyLine()])
    }

    function addLine() {
        onChange([...lines, newEmptyLine()])
    }

    function addDiscountLine() {
        const vat = lines.length > 0 ? lines[lines.length - 1].vat : DropdownVATOptions.STANDARD
        onChange([...lines, newEmptyLine({description: 'Korting', vat})])
    }

    return (
        <section id="lines-section" tabIndex={-1}>
            <h2 className="text-xl font-bold">Diensten &amp; goederen</h2>
            <p className="text-xs text-gray-600 mt-1 mb-4">
                Wat is er geleverd? Voeg per dienst of product een regel toe — het totaal en de btw
                worden automatisch berekend. Btw mag per regel verschillen.
            </p>

            <div className="overflow-x-auto">
                <table className="w-full min-w-[760px] text-sm border-separate border-spacing-y-2 align-middle">
                    <thead>
                        <tr>
                            <th className="py-2 px-3 text-left font-medium text-gray-700 bg-gray-100 rounded-l-lg">
                                Omschrijving<span className="text-red-500 ml-1">*</span>
                            </th>
                            <th className="py-2 px-2 text-right font-medium text-gray-700 bg-gray-100 w-20">Aantal</th>
                            <th className="py-2 px-2 text-left font-medium text-gray-700 bg-gray-100 w-28">Eenheid</th>
                            <th className="py-2 px-2 text-right font-medium text-gray-700 bg-gray-100 w-32">
                                Stukprijs<span className="text-red-500 ml-1">*</span>
                            </th>
                            <th className="py-2 px-2 text-left font-medium text-gray-700 bg-gray-100 w-32">Btw</th>
                            <th className="py-2 px-3 text-right font-medium text-gray-700 bg-gray-100 w-28">Totaal</th>
                            <th className="py-2 px-1 font-medium text-gray-700 bg-gray-100 rounded-r-lg w-10"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {lines.map((line) => (
                            <tr key={line.id}>
                                <td className="pr-2">
                                    <LineDescriptionField
                                        value={line.description}
                                        catalogItems={catalogItems}
                                        saveToCatalogue={line.saveToCatalogue}
                                        onDescriptionChange={(description) => changeDescription(line, description)}
                                        onPickCatalogItem={(item) => pickCatalogItem(line, item)}
                                        onToggleSaveToCatalogue={(saveToCatalogue) =>
                                            updateLine(line.id, {saveToCatalogue})}
                                    />
                                </td>
                                <td className="pr-2">
                                    <input
                                        type="number"
                                        min={0}
                                        step="any"
                                        className={cn(fieldVariants(), 'text-right')}
                                        value={line.quantity ?? ''}
                                        onChange={(e) => {
                                            const raw = e.target.value
                                            updateLine(line.id, {quantity: raw === '' ? null : Number(raw)})
                                        }}
                                    />
                                </td>
                                <td className="pr-2">
                                    <Dropdown
                                        selected={unitOptions.find(opt => opt.value === line.unit) ?? unitOptions[0]}
                                        options={unitOptions}
                                        onUpdateFunc={(value) => updateLine(line.id, {unit: value as InvoiceLineItem['unit']})}
                                    />
                                </td>
                                <td className="pr-2">
                                    <MoneyInput
                                        locale={DisplayLocale.NL_NL}
                                        currency={DEFAULT_CURRENCY}
                                        minimumFractionDigits={2}
                                        maximumFractionDigits={2}
                                        className="!h-9 text-right"
                                        selectOnFocus
                                        value={line.unitPrice != null && isDecimalInSafeRange(line.unitPrice)
                                            ? fromDecimalToMoney(line.unitPrice, DEFAULT_CURRENCY)
                                            : undefined}
                                        onChange={(m) => updateLine(line.id, {unitPrice: toDecimalFromMoney(m)})}
                                    />
                                </td>
                                <td className="pr-2">
                                    <Dropdown
                                        selected={vatOptions.find(opt => opt.value === line.vat) ?? vatOptions[0]}
                                        options={vatOptions}
                                        onUpdateFunc={(value) => updateLine(line.id, {vat: value as DropdownVATOptions})}
                                    />
                                </td>
                                <td className="px-3 text-right font-semibold whitespace-nowrap">
                                    € {decimalToLocalizedString(lineTotal(line))}
                                </td>
                                <td className="px-1 text-center">
                                    <button
                                        type="button"
                                        onClick={() => deleteLine(line.id)}
                                        aria-label="Regel verwijderen"
                                        className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"
                                    >
                                        <TrashIcon className="h-4 w-4"/>
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="flex items-center gap-3 mt-1">
                <button
                    type="button"
                    onClick={addLine}
                    className="flex items-center gap-1.5 rounded-md border border-dashed border-gray-300 px-3 py-1.5 text-sm font-semibold text-primary hover:border-[color:var(--primary)] transition-colors"
                >
                    <PlusIcon className="h-4 w-4"/>
                    Regel toevoegen
                </button>
                <button
                    type="button"
                    onClick={addDiscountLine}
                    className="flex items-center gap-1.5 rounded-md border border-dashed border-gray-300 px-3 py-1.5 text-sm font-semibold text-primary hover:border-[color:var(--primary)] transition-colors"
                >
                    <ReceiptPercentIcon className="h-4 w-4"/>
                    Kortingsregel
                </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
                Een korting voer je in als negatieve stukprijs, bijvoorbeeld <span className="font-semibold">-125,00</span>.
                {catalogItems.length > 0 && ' Begin met typen om een product of dienst uit je catalogus over te nemen.'}
            </p>

            {linesError && (
                <p className="mt-2 text-xs text-red-600">{linesError}</p>
            )}
        </section>
    )
}
